import { useEffect, useMemo, useState } from "react";

import { BusinessProfileControllerService } from "@zenith/services/user/services/BusinessProfileControllerService";

import {
  can,
  scopeFor,
  type BusinessType,
  type OutletScope,
  type Permission,
  type TeamRole,
} from "./permissions";

const toTeamRole = (role?: string): TeamRole => {
  switch ((role ?? "").toLowerCase()) {
    case "manager":
      return "manager";
    case "accountant":
      return "accountant";
    case "cashier":
      return "cashier";
    default:
      // Owners and admins both land here, and both hold every permission.
      return "admin";
  }
};

const toBusinessType = (value?: string): BusinessType =>
  (value ?? "").toUpperCase() === "MULTI_SIGNATORY" ? "MS" : "IND";

/**
 * The role and business type of the context the caller is currently in, and
 * the questions a screen asks of them.
 *
 * BOAS-7641 Scenario 5: "user should ONLY see the menu, pages, buttons, and
 * sections that they have access to". Everything that hides or blocks part of
 * the app asks this hook rather than inspecting the role itself, so the matrix
 * stays in one file.
 *
 * Until the profile resolves it answers as the role with the fewest
 * permissions, never as an owner. Answering as an owner meant a team member
 * saw the owner's menu for as long as the profile took to arrive - the one
 * direction of error that shows someone a screen they may not have. Screens
 * that must not redirect before the role is known wait on `resolved`.
 */
export const usePermissions = (): {
  role: TeamRole;
  businessType: BusinessType;
  resolved: boolean;
  can: (permission: Permission) => boolean;
  scopeFor: (permission: Permission) => OutletScope;
} => {
  const [role, setRole] = useState<TeamRole>("cashier");
  const [businessType, setBusinessType] = useState<BusinessType>("IND");
  const [resolved, setResolved] = useState<boolean>(false);

  useEffect(() => {
    let isCurrent = true;
    BusinessProfileControllerService.current()
      .then((response) => {
        if (!isCurrent) return;
        setRole(toTeamRole(response.data?.profile?.role));
        setBusinessType(toBusinessType(response.data?.profile?.businessType));
        setResolved(true);
      })
      .catch((error) => {
        console.error("Failed to read the current profile role:", error);
        // Nothing to grant on a failed read: the least-privilege default
        // stands rather than opening the app up.
        if (isCurrent) setResolved(true);
      });
    return () => {
      isCurrent = false;
    };
  }, []);

  return useMemo(
    () => ({
      role,
      businessType,
      resolved,
      can: (permission: Permission) => can(role, businessType, permission),
      scopeFor: (permission: Permission) =>
        scopeFor(role, businessType, permission),
    }),
    [role, businessType, resolved]
  );
};
